import { Controller } from 'stimulus'
import './field_component.scss'
import { measure, mutate } from '~/common/helpers/layout_batch'
import { shouldStackLabel } from './field_component_label_stacking'

export default class extends Controller {
  connect() {
    const selectField = this.element.querySelector('.select-with-inner-label')
    if (selectField) {
      this.setSelectTagPadding(selectField)
    }

    this.measureLabelStacking()
  }

  disconnect() {
    this.labelStackingVisibilityObserver?.disconnect()
  }

  // ENG-34476: decides whether this field's inline label needs its own row instead of squeezing
  // the control. Only meaningful inside the builder settings sidebar, where forms.css fixes the
  // label column at 120px — everywhere else --t-field-inline-label-width is unset and this is a
  // cheap no-op, since this controller runs on every Form::FieldComponent app-wide.
  measureLabelStacking() {
    if (!this.element.classList.contains('t-field--label-style-inline')) return
    if (!this.element.closest('.builder-editor-sidebar')) return

    const labelColumn = this.element.querySelector(
      ':scope > .t-field__body-left'
    )
    if (!labelColumn) return

    // A field inside a switch's collapsed content (visibility conditions, button action
    // sub-fields) is display:none at connect() time — everything measures 0 there, and a plain
    // CSS reveal (revealable_controller's .hidden toggle) never reconnects Stimulus to give us a
    // second chance. Defer to when it's actually visible instead of measuring blind.
    if (this.element.offsetParent === null) {
      this.labelStackingVisibilityObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return
          this.labelStackingVisibilityObserver.disconnect()
          this.scheduleLabelMeasurement(labelColumn)
        }
      )
      this.labelStackingVisibilityObserver.observe(this.element)
    } else {
      this.scheduleLabelMeasurement(labelColumn)
    }
  }

  scheduleLabelMeasurement(labelColumn) {
    // Measuring before a webfont swaps in gives widths for the fallback face, which can put a
    // borderline label (Button label at 101px) on the wrong side of the threshold.
    // document.fonts.ready resolves immediately once already loaded.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.applyLabelStacking(labelColumn))
    } else {
      this.applyLabelStacking(labelColumn)
    }
  }

  applyLabelStacking(labelColumn) {
    // labelColumn is pinned to the shared column (flex-basis + flex-shrink:0), so its rendered
    // width is always ~120px regardless of content — text just wraps inside that box. Measure its
    // true content width by briefly relaxing those constraints on the real node, then restore —
    // not an off-screen clone, which would lose the sidebar's font-size cascade and mismeasure.
    const { cssText } = labelColumn.style
    labelColumn.style.flexBasis = 'auto'
    labelColumn.style.whiteSpace = 'nowrap'

    // The width write above must land before it's read, but the read itself has to go through
    // measure() — calling getBoundingClientRect() straight here would force its own layout
    // recalculation, and a panel with a dozen inline fields (this controller runs on every one)
    // would force a dozen back-to-back reflows on connect instead of the one measure()/mutate()
    // exists to produce.
    measure(() => {
      const naturalWidth = labelColumn.getBoundingClientRect().width
      const stacked = shouldStackLabel(naturalWidth)
      mutate(() => {
        labelColumn.style.cssText = cssText
        this.element.classList.toggle('t-field--stacked-inline-label', stacked)
      })
    })
  }

  // A hack to focus the control in case the user clicks on the padding of the body or someother artifact like the prefix/suffix
  focusControl(e) {
    if (
      e.target.closest(
        'input,textarea,select,a,button,.tippy-box,[contenteditable],.tiptap,.prosemirror-editor,.t-field__control-wrapper'
      ) ||
      !e.target.parentElement
    )
      return

    const controlWrapper = this.element.querySelector(
      '.t-field__control-wrapper'
    )
    const focusable = controlWrapper?.querySelector(
      'input:not([type=hidden]),textarea,select'
    )

    /* Initially i had a general dropdown selector here, but it will break certain composite fields like tiptap, which may contain a dropdown within. */
    /* So make sure when adding something general here, doesn't break such fields like WYSIWYG editors... */
    const dropdown = controlWrapper.querySelector(
      '.js-time-zone-select-dropdown'
    )

    if (dropdown) {
      this.dispatch('dropdown:open', { target: dropdown, prefix: false })
    } else if (focusable) {
      focusable.focus()
    }
  }

  // A hack that allows us to have a select tag with a inner clickable preffix or suffix. e.g [Participants: 1]
  // When a select field component has a prefix or suffix, 'select-with-inner-label' class is added to the field body
  setSelectTagPadding(selectField) {
    const select = selectField.querySelector('select')
    const prefix = selectField.querySelector('.t-field__prefix')
    const suffix = selectField.querySelector('.t-field__suffix')

    if (prefix) {
      select.style.paddingLeft = prefix.offsetWidth - 5 + 'px'
    }

    if (suffix) {
      suffix.style.paddingLeft = select.offsetWidth + 'px'
      select.style.paddingRight = suffix.offsetWidth + 'px'
    }
  }
}
